// The boat's operator token, as its owners see it in the boat's Settings
// (TOM-294, cloud ADR 0420; contract §5.27). Pure helpers, so the panel's
// words are tested without a DOM.

/**
 * While the boat has not taken the newest token, ask again after 5 s, then
 * twice as long each time up to a minute, and stop after ten asks (about six
 * minutes). The page asks again when the tab comes back, or on a rotation.
 */
export const OPERATOR_TOKEN_POLL_FIRST_MS = 5000;
export const OPERATOR_TOKEN_POLL_MAX_MS = 60_000;
export const OPERATOR_TOKEN_POLL_MAX_ASKS = 10;

/** The wait before ask number `asked + 1`, or null once the asks are spent. */
export function operatorTokenPollDelayMs(asked: number): number | null {
  if (asked >= OPERATOR_TOKEN_POLL_MAX_ASKS) return null;
  return Math.min(OPERATOR_TOKEN_POLL_FIRST_MS * 2 ** asked, OPERATOR_TOKEN_POLL_MAX_MS);
}

export type OperatorTokenStatus = 'none' | 'waiting' | 'applied' | 'unreadable';

export type OperatorTokenKind = 'claim' | 'owner' | 'repair' | 'owner_removed';
const KINDS: readonly OperatorTokenKind[] = ['claim', 'owner', 'repair', 'owner_removed'];

/** `GET /v1/boats/:boatId/operator-token`. Never the token itself. */
export interface OperatorTokenView {
  configured: boolean;
  version: number | null;
  issuedAt: string | null;
  /** Why the current token was made (cloud ADR 0420). */
  issuedFor: OperatorTokenKind | null;
  status: OperatorTokenStatus;
  boatVersion: number | null;
  boatReportedAt: string | null;
  needsCoreVersion: string | null;
}

function nullableString(v: unknown): v is string | null {
  return v === null || typeof v === 'string';
}

function nullableNumber(v: unknown): v is number | null {
  return v === null || (typeof v === 'number' && Number.isFinite(v));
}

/** The server's view, or null when it is not one. */
export function parseOperatorTokenView(raw: unknown): OperatorTokenView | null {
  if (raw === null || typeof raw !== 'object') return null;
  const v = raw as Record<string, unknown>;
  if (
    typeof v.configured !== 'boolean' ||
    !nullableNumber(v.version) ||
    !nullableString(v.issuedAt) ||
    !(v.issuedFor === null || KINDS.includes(v.issuedFor as OperatorTokenKind)) ||
    !(
      v.status === 'none' ||
      v.status === 'waiting' ||
      v.status === 'applied' ||
      v.status === 'unreadable'
    ) ||
    !nullableNumber(v.boatVersion) ||
    !nullableString(v.boatReportedAt) ||
    !nullableString(v.needsCoreVersion)
  ) {
    return null;
  }
  return {
    configured: v.configured,
    version: v.version,
    issuedAt: v.issuedAt,
    issuedFor: v.issuedFor as OperatorTokenKind | null,
    status: v.status,
    boatVersion: v.boatVersion,
    boatReportedAt: v.boatReportedAt,
    needsCoreVersion: v.needsCoreVersion,
  };
}

export function operatorTokenTime(iso: string, locale?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}

/** The one status line. */
export function operatorTokenStatusLine(
  view: OperatorTokenView,
  boatName: string,
  locale?: string
): string {
  if (view.needsCoreVersion !== null) {
    return `Needs Neomaris Core ${view.needsCoreVersion} or later on the boat. Until then, the boat's mode controls are open to anyone on its network.`;
  }
  switch (view.status) {
    case 'none':
      return `${boatName} gets its token the next time it connects.`;
    case 'waiting':
      // The first token has nothing before it: the boat is open until it takes it.
      return view.boatVersion === null
        ? `Waiting for ${boatName} to take its token. Until it does, its mode controls and operator pages are open to anyone on its network.`
        : `Waiting for ${boatName} to take the newest token. Until it does, the previous token still works on the boat.`;
    case 'unreadable':
      // Fail-closed on the boat: it refuses everyone, it is not open.
      return `${boatName} can't read its stored token, so it refuses everyone on its network. Connect it to the internet so it can take the token again, or set NEOMARIS_CORE_ADMIN_TOKEN on the boat computer.`;
    case 'applied':
      return view.boatReportedAt !== null
        ? `${boatName} requires this token. Confirmed ${operatorTokenTime(view.boatReportedAt, locale)}.`
        : `${boatName} requires this token.`;
  }
}

/** Poll while the boat has not said it holds the newest token. */
export function shouldPollOperatorToken(view: OperatorTokenView | null): boolean {
  return view !== null && view.needsCoreVersion === null && view.status !== 'applied';
}

export function operatorTokenErrorMessage(status: number, body: unknown): string {
  const code =
    body !== null &&
    typeof body === 'object' &&
    typeof (body as { error?: unknown }).error === 'string'
      ? (body as { error: string }).error
      : null;
  if (code === 'no_token') return 'The boat has no token yet. It gets one when it next connects.';
  if (status === 401) return 'Sign in again, then try again.';
  if (status === 403) return 'Reload the page, then try again.';
  if (status === 404) return 'This boat is no longer on your account.';
  if (status === 429) return 'Too many requests at once. Try again in a minute.';
  return 'That didn’t work. Try again.';
}

/** Whether a token revealed at `revealedVersion` is still the view's current one. */
export function revealedTokenIsCurrent(
  view: OperatorTokenView,
  revealedVersion: number | null
): boolean {
  return view.configured && revealedVersion !== null && view.version === revealedVersion;
}
